import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type CollectionResponse, type ResourceResponse } from "@shared/api/client";
import type {
  Agenda,
  Alerta,
  Automatizacion,
  AutomatizacionFormData,
  Campana,
  Formulario,
  FormularioFormData,
  FormularioPublico,
  CampanaFormData,
  Contacto,
  ContactoFormData,
  Contenido,
  ContenidoFormData,
  Etapa,
  Lista,
  MarketingDashboard,
  Oportunidad,
  PipelineColumn,
  Plantilla,
  SalesAnalytics,
  ScoringRegla,
  ScoringReglaFormData,
} from "../types";

const BASE = "/marketing";

export const mktKeys = {
  all: ["marketing"] as const,
  dashboard: (e: number) => [...mktKeys.all, "dashboard", e] as const,
  contacts: (e: number, f?: unknown) => [...mktKeys.all, "contacts", e, f ?? {}] as const,
  contact: (e: number, id: number) => [...mktKeys.all, "contact", e, id] as const,
  board: (e: number) => [...mktKeys.all, "board", e] as const,
  stages: (e: number) => [...mktKeys.all, "stages", e] as const,
  campaigns: (e: number, f?: unknown) => [...mktKeys.all, "campaigns", e, f ?? {}] as const,
  lists: (e: number) => [...mktKeys.all, "lists", e] as const,
  templates: (e: number) => [...mktKeys.all, "templates", e] as const,
  content: (e: number, f?: unknown) => [...mktKeys.all, "content", e, f ?? {}] as const,
  automations: (e: number) => [...mktKeys.all, "automations", e] as const,
  scoringRules: (e: number) => [...mktKeys.all, "scoring-rules", e] as const,
  alerts: (e: number) => [...mktKeys.all, "alerts", e] as const,
  forms: (e: number) => [...mktKeys.all, "forms", e] as const,
  analytics: (e: number) => [...mktKeys.all, "analytics", e] as const,
  agenda: (e: number) => [...mktKeys.all, "agenda", e] as const,
};

// ─────────────── Dashboard ───────────────
export function useMktDashboard(emisorId: number | null) {
  return useQuery({
    queryKey: mktKeys.dashboard(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<MarketingDashboard>>(`${BASE}/dashboard`, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

// ─────────────── Contactos ───────────────
export function useContactos(
  emisorId: number | null,
  filters?: { estado?: string; fuente?: string; q?: string },
) {
  return useQuery({
    queryKey: mktKeys.contacts(emisorId ?? 0, filters),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Contacto> & { meta: { fuentes: string[] } }>(
        `${BASE}/contacts`,
        { params: { emisor_id: emisorId, ...filters } },
      );
      return data;
    },
    enabled: !!emisorId,
  });
}

export function useContacto(emisorId: number | null, id: number | null) {
  return useQuery({
    queryKey: mktKeys.contact(emisorId ?? 0, id ?? 0),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<Contacto>>(`${BASE}/contacts/${id}`, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    enabled: !!emisorId && !!id,
  });
}

export function useCreateContacto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ emisorId, payload }: { emisorId: number; payload: ContactoFormData }) => {
      const { data } = await api.post<ResourceResponse<Contacto>>(`${BASE}/contacts`, payload, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useUpdateContacto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<ContactoFormData> }) => {
      const { data } = await api.patch<ResourceResponse<Contacto>>(`${BASE}/contacts/${id}`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useDeleteContacto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/contacts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useImportarClientes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (emisorId: number) => {
      const { data } = await api.post<{ data: { importados: number; omitidos: number } }>(
        `${BASE}/contacts/import-clients`,
        null,
        { params: { emisor_id: emisorId } },
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useAddActividad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      emisorId,
      contactoId,
      payload,
    }: {
      emisorId: number;
      contactoId: number;
      payload: { tipo: string; asunto: string; descripcion?: string; fecha?: string };
    }) => {
      const { data } = await api.post(`${BASE}/contacts/${contactoId}/activities`, payload, {
        params: { emisor_id: emisorId },
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

// ─────────────── Pipeline ───────────────
export function usePipelineBoard(emisorId: number | null) {
  return useQuery({
    queryKey: mktKeys.board(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<PipelineColumn>>(`${BASE}/pipeline/board`, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useEtapas(emisorId: number | null) {
  return useQuery({
    queryKey: mktKeys.stages(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Etapa>>(`${BASE}/pipeline/stages`, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useCreateOportunidad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      emisorId,
      payload,
    }: {
      emisorId: number;
      payload: Record<string, unknown>;
    }) => {
      const { data } = await api.post<ResourceResponse<Oportunidad>>(`${BASE}/opportunities`, payload, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useUpdateOportunidad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Record<string, unknown> }) => {
      const { data } = await api.patch<ResourceResponse<Oportunidad>>(
        `${BASE}/opportunities/${id}`,
        payload,
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useMoverOportunidad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, etapaId }: { id: number; etapaId: number }) => {
      const { data } = await api.post<ResourceResponse<Oportunidad>>(
        `${BASE}/opportunities/${id}/move`,
        { etapa_id: etapaId },
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useConvertirOportunidad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ ok: boolean; cliente_id: number }>(
        `${BASE}/opportunities/${id}/convert`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useDeleteOportunidad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/opportunities/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

// ─────────────── Campañas + listas ───────────────
export function useCampanas(emisorId: number | null, filters?: { canal?: string; estado?: string }) {
  return useQuery({
    queryKey: mktKeys.campaigns(emisorId ?? 0, filters),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Campana>>(`${BASE}/campaigns`, {
        params: { emisor_id: emisorId, ...filters },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useCreateCampana() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ emisorId, payload }: { emisorId: number; payload: CampanaFormData }) => {
      const { data } = await api.post<ResourceResponse<Campana>>(`${BASE}/campaigns`, payload, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useUpdateCampana() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<CampanaFormData> }) => {
      const { data } = await api.patch<ResourceResponse<Campana>>(`${BASE}/campaigns/${id}`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useDeleteCampana() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/campaigns/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useEnviarCampana() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ ok: boolean; enviados: number; errores: number }>(
        `${BASE}/campaigns/${id}/send`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useGanadorAB() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{
        data: { variantes: Record<string, { enviados: number; abiertos: number; tasa: number }>; ganador: string | null };
      }>(`${BASE}/campaigns/${id}/ab-winner`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useListas(emisorId: number | null) {
  return useQuery({
    queryKey: mktKeys.lists(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Lista>>(`${BASE}/lists`, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useCreateLista() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      emisorId,
      payload,
    }: {
      emisorId: number;
      payload: { nombre: string; descripcion?: string; tipo?: string };
    }) => {
      const { data } = await api.post<ResourceResponse<Lista>>(`${BASE}/lists`, payload, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

// ─────────────── Plantillas ───────────────
export function usePlantillas(emisorId: number | null) {
  return useQuery({
    queryKey: mktKeys.templates(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Plantilla>>(`${BASE}/templates`, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useCreatePlantilla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      emisorId,
      payload,
    }: {
      emisorId: number;
      payload: { nombre: string; asunto: string; html?: string };
    }) => {
      const { data } = await api.post<ResourceResponse<Plantilla>>(`${BASE}/templates`, payload, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useDeletePlantilla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

// ─────────────── Contenidos ───────────────
export function useContenidos(
  emisorId: number | null,
  filters?: { estado?: string; canal?: string; mes?: string },
) {
  return useQuery({
    queryKey: mktKeys.content(emisorId ?? 0, filters),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Contenido>>(`${BASE}/content`, {
        params: { emisor_id: emisorId, ...filters },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useCreateContenido() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ emisorId, payload }: { emisorId: number; payload: ContenidoFormData }) => {
      const { data } = await api.post<ResourceResponse<Contenido>>(`${BASE}/content`, payload, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useUpdateContenido() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<ContenidoFormData> }) => {
      const { data } = await api.patch<ResourceResponse<Contenido>>(`${BASE}/content/${id}`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useDeleteContenido() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/content/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

// ─────────────── Automatizaciones ───────────────
export function useAutomatizaciones(emisorId: number | null) {
  return useQuery({
    queryKey: mktKeys.automations(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Automatizacion>>(`${BASE}/automations`, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useCreateAutomatizacion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      emisorId,
      payload,
    }: {
      emisorId: number;
      payload: AutomatizacionFormData;
    }) => {
      const { data } = await api.post<ResourceResponse<Automatizacion>>(
        `${BASE}/automations`,
        payload,
        { params: { emisor_id: emisorId } },
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useUpdateAutomatizacion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<AutomatizacionFormData> }) => {
      const { data } = await api.patch<ResourceResponse<Automatizacion>>(
        `${BASE}/automations/${id}`,
        payload,
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useToggleAutomatizacion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<ResourceResponse<Automatizacion>>(
        `${BASE}/automations/${id}/toggle`,
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useDeleteAutomatizacion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/automations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useRunInactivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (emisorId: number) => {
      const { data } = await api.post<{ ok: boolean; procesados: number }>(
        `${BASE}/automations/run-inactivity`,
        null,
        { params: { emisor_id: emisorId } },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

// ─────────────── Lead scoring ───────────────
export function useScoringRules(emisorId: number | null) {
  return useQuery({
    queryKey: mktKeys.scoringRules(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<ScoringRegla>>(`${BASE}/scoring/rules`, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useCreateScoringRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ emisorId, payload }: { emisorId: number; payload: ScoringReglaFormData }) => {
      const { data } = await api.post<ResourceResponse<ScoringRegla>>(`${BASE}/scoring/rules`, payload, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useUpdateScoringRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<ScoringReglaFormData> }) => {
      const { data } = await api.patch<ResourceResponse<ScoringRegla>>(`${BASE}/scoring/rules/${id}`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useDeleteScoringRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/scoring/rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useRecalcularScoring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (emisorId: number) => {
      const { data } = await api.post<{ ok: boolean; actualizados: number }>(
        `${BASE}/scoring/recalculate`,
        null,
        { params: { emisor_id: emisorId } },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

// ─────────────── Alertas ───────────────
export function useAlertas(emisorId: number | null) {
  return useQuery({
    queryKey: mktKeys.alerts(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Alerta> & { meta: { no_leidas: number } }>(
        `${BASE}/alerts`,
        { params: { emisor_id: emisorId } },
      );
      return data;
    },
    enabled: !!emisorId,
  });
}

export function useGenerarAlertas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (emisorId: number) => {
      const { data } = await api.post<{ ok: boolean; leads_calientes: number; oportunidades_estancadas: number }>(
        `${BASE}/alerts/generate`,
        null,
        { params: { emisor_id: emisorId } },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useMarcarAlertaLeida() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.post(`${BASE}/alerts/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useMarcarTodasLeidas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (emisorId: number) =>
      api.post(`${BASE}/alerts/read-all`, null, { params: { emisor_id: emisorId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useDeleteAlerta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/alerts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

// ─────────────── Formularios de captación ───────────────
export function useFormularios(emisorId: number | null) {
  return useQuery({
    queryKey: mktKeys.forms(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Formulario>>(`${BASE}/forms`, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useCreateFormulario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ emisorId, payload }: { emisorId: number; payload: FormularioFormData }) => {
      const { data } = await api.post<ResourceResponse<Formulario>>(`${BASE}/forms`, payload, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useUpdateFormulario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<FormularioFormData> }) => {
      const { data } = await api.patch<ResourceResponse<Formulario>>(`${BASE}/forms/${id}`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useDeleteFormulario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/forms/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

// ─────────────── Analytics de ventas ───────────────
export function useSalesAnalytics(emisorId: number | null) {
  return useQuery({
    queryKey: mktKeys.analytics(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<SalesAnalytics>>(`${BASE}/analytics`, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

// ─────────────── Agenda comercial ───────────────
export function useAgenda(emisorId: number | null) {
  return useQuery({
    queryKey: mktKeys.agenda(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<Agenda>>(`${BASE}/agenda`, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useCrearTarea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      emisorId,
      payload,
    }: {
      emisorId: number;
      payload: { asunto: string; descripcion?: string; tipo?: string; fecha?: string; contacto_id?: number };
    }) => {
      const { data } = await api.post(`${BASE}/tasks`, payload, { params: { emisor_id: emisorId } });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useCompletarTarea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.post(`${BASE}/tasks/${id}/complete`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

export function useEliminarTarea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/tasks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mktKeys.all }),
  });
}

// ─── Públicos (landing) ───
export async function fetchFormularioPublico(slug: string): Promise<FormularioPublico> {
  const { data } = await api.get<ResourceResponse<FormularioPublico>>(
    `${BASE}/public/forms/${slug}`,
  );
  return data.data;
}

export async function submitFormularioPublico(
  slug: string,
  datos: Record<string, string>,
): Promise<{ ok: boolean; mensaje: string }> {
  const { data } = await api.post<{ ok: boolean; mensaje: string }>(
    `${BASE}/public/forms/${slug}`,
    datos,
  );
  return data;
}
